import { Component, Inject } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Helpers } from '../../../../../../utilities/helpers';

export interface DeleteBoardMemberItem {
  /** Caller-supplied identifier echoed back per-member in the result */
  id: string;
  /** Display name */
  name: string;
  /** Whether this specific member should expose the CEO end-date field */
  showCeoEndDate: boolean;
  /** Existing departure date already recorded on the appointment (ISO), pre-filled into End Date */
  endDate?: string;
  /** Existing CEO end date already recorded on the appointment (ISO), pre-filled into CEO End Date */
  ceoEndDate?: string;
}

export interface DeleteBoardMemberDialogData {
  members: DeleteBoardMemberItem[];
  year: string;
  isExecutive: boolean;
}

export interface DeleteBoardMemberMemberResult {
  id: string;
  endDate?: string;
  ceoEndDate?: string;
}

export interface DeleteBoardMemberDialogResult {
  members: DeleteBoardMemberMemberResult[];
}

@Component({
  selector: 'app-delete-board-member-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    FormsModule
  ],
  templateUrl: './delete-board-member-dialog.component.html',
  styleUrls: ['./delete-board-member-dialog.component.scss']
})
export class DeleteBoardMemberDialogComponent {
  /** Parallel arrays keyed by member index */
  endDateDisplays: string[];
  ceoEndDateDisplays: string[];

  constructor(
    public dialogRef: MatDialogRef<DeleteBoardMemberDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DeleteBoardMemberDialogData
  ) {
    // Pre-fill with the date already recorded on the appointment (e.g. entered via Fix Directors)
    // so the user doesn't have to re-enter it. Leaving it as-is keeps the existing date.
    this.endDateDisplays = data.members.map(m => Helpers.toDisplayDate(m.endDate));
    this.ceoEndDateDisplays = data.members.map(m => m.showCeoEndDate ? Helpers.toDisplayDate(m.ceoEndDate) : '');
  }

  get dialogTitle(): string {
    const role = this.data.isExecutive ? 'Executive Member' : 'Board Member';
    return this.isMultiple ? `Remove ${role}s` : `Remove ${role}`;
  }

  get isMultiple(): boolean {
    return this.data.members.length > 1;
  }

  /** Whether the CEO end-date column should appear at all */
  get anyShowCeoEndDate(): boolean {
    return this.data.members.some(m => m.showCeoEndDate);
  }

  onDateInput(event: Event, index: number, field: 'endDate' | 'ceoEndDate'): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9/]/g, '');

    if (value.length === 2 && !value.includes('/')) {
      value += '/';
    } else if (value.length === 5 && value.indexOf('/', 3) === -1) {
      value += '/';
    }

    input.value = value;
    if (field === 'endDate') {
      this.endDateDisplays[index] = value;
    } else {
      this.ceoEndDateDisplays[index] = value;
    }
  }

  onDateFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (/^\d{4}$/.test(input.value)) {
      input.value = `01/01/${input.value}`;
    }
  }

  onDateBlur(event: Event, index: number, field: 'endDate' | 'ceoEndDate'): void {
    const input = event.target as HTMLInputElement;
    if (/^01\/01\/\d{4}$/.test(input.value)) {
      input.value = input.value.substring(6);
    }
    if (field === 'endDate') {
      this.endDateDisplays[index] = input.value;
    } else {
      this.ceoEndDateDisplays[index] = input.value;
    }
  }

  onConfirm(): void {
    const members: DeleteBoardMemberMemberResult[] = this.data.members.map((m, i) => {
      const entry: DeleteBoardMemberMemberResult = { id: m.id };
      const endIso = this.toIsoDate(this.endDateDisplays[i]);
      if (endIso) entry.endDate = endIso;
      if (m.showCeoEndDate) {
        const ceoEndIso = this.toIsoDate(this.ceoEndDateDisplays[i]);
        if (ceoEndIso) entry.ceoEndDate = ceoEndIso;
      }
      return entry;
    });
    this.dialogRef.close({ members });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private toIsoDate(displayDate: string): string {
    if (!displayDate) return '';
    if (/^\d{4}$/.test(displayDate)) return `${displayDate}-01-01T00:00:00Z`;
    const parts = displayDate.split('/');
    if (parts.length !== 3 || parts[2].length !== 4) return '';
    const [day, month, year] = parts;
    return `${year}-${month}-${day}T00:00:00Z`;
  }
}
